// hooks/useExplore.ts — hook for the Explore page data.
//
// Pure read-only (no store — explore feed is static in prototype phase).
// Uses React.useState + useEffect to load async data from exploreService.
// Components must NOT import exploreService directly.

import { useState, useEffect } from 'react';
import { exploreService } from '@/core/services/exploreService';
import type { ExploreTopic, ExploreItem, FeaturedCollection } from '@/core/models/explore';
import type { ExploreSort } from '@/core/services/exploreService';

export interface ExploreHook {
  /** All topic filter chips. */
  topics: ExploreTopic[];
  /** Feed items filtered + sorted by the given topic/sort. */
  feed: ExploreItem[];
  /** Featured collection tiles. */
  featuredCollections: FeaturedCollection[];
  /** Whether data has finished loading. */
  loaded: boolean;
}

/**
 * Primary hook: explore feed + topics + collections.
 * Reloads feed whenever topicId or sort changes.
 */
export function useExplore(topicId: string = 'all', sort: ExploreSort = 'recent'): ExploreHook {
  const [topics, setTopics] = useState<ExploreTopic[]>([]);
  const [feed, setFeed] = useState<ExploreItem[]>([]);
  const [featuredCollections, setFeaturedCollections] = useState<FeaturedCollection[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void Promise.all([
      exploreService.topics(),
      exploreService.feed(topicId, sort),
      exploreService.featuredCollections(),
    ]).then(([t, f, c]) => {
      setTopics(t);
      setFeed(f);
      setFeaturedCollections(c);
      setLoaded(true);
    });
  }, [topicId, sort]);

  return { topics, feed, featuredCollections, loaded };
}
